// Chinese Checkers interface. Tap one of your marbles, then a hole marked with a dot: the marble hops there, one jump
// at a time, along the shortest route. Tap another of your marbles to change your mind. The computer's last move stays
// marked until yours. Undo takes back your last move and the computer's reply. The star and the starting marbles are
// in the server HTML (games/boards/_chinese_checkers.html.erb), so nothing moves on load.
import { computerMove, HOLES, hintMove, holeName, home, legalMoves, LEVELS, movesFrom, newGame, play, type CCState, type Level, type Move } from "./chinese-checkers"
import { readJSON, writeJSON } from "../lib/store"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

const SAVE = "dt:save:chinese-checkers:v1"
const RULESET = "chinese-checkers"
interface Saved { v: 1; state: CCState; history: CCState[]; level: Level; game: number; last: Move | null }

export class ChineseCheckersGame {
  private s: CCState = newGame()
  private history: CCState[] = []
  private level: Level = "medium"
  private game = 0
  private last: Move | null = null
  private sel: number | null = null
  private hint: Move | null = null
  private busy = false
  private sentFirst = false
  private hints = 0
  private undos = 0
  private focus = 111
  private hop: { from: number; at: number } | null = null // a marble part way along its route, on screen
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly board: HTMLElement

  constructor(private readonly root: HTMLElement) {
    this.board = root.querySelector<HTMLElement>("[data-cc-board]")!
    const saved = readJSON<Saved>(SAVE)
    if (saved?.v === 1 && saved.state?.board?.length === HOLES.length) {
      this.s = saved.state; this.history = saved.history ?? []; this.level = LEVELS.includes(saved.level) ? saved.level : "medium"
      this.game = saved.game; this.last = saved.last ?? null
      if (!this.s.over) this.say("Welcome back. Your game is where you left it.")
    } else {
      this.game = newAttempt()
    }
    const select = document.querySelector<HTMLSelectElement>("select[name=cc-level]")
    if (select) {
      select.value = this.level
      select.addEventListener("change", () => {
        this.level = select.value as Level
        this.persist()
        this.say(`Computer: ${select.selectedOptions[0]?.textContent ?? this.level}.`)
      })
    }
    this.board.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("[data-hole]")
      if (b) this.tap(Number(b.dataset.hole))
    })
    this.board.addEventListener("keydown", (e) => this.onKey(e))
    document.querySelector("[data-toolbar]")?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "undo") this.undo()
      else if (act === "hint") this.showHint()
      else if (act === "new") this.newGame()
    })
    document.addEventListener("keydown", (e) => {
      if ((e.target as HTMLElement).closest?.("input, select, textarea")) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); this.undo() }
      else if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "h") this.showHint()
    })
    this.render()
    if (this.s.turn === "cpu" && !this.s.over) this.computerTurn()
  }

  private get motion(): boolean { return document.documentElement.dataset.motion !== "off" }

  private persist(): void {
    writeJSON(SAVE, { v: 1, state: this.s, history: this.history.slice(-80), level: this.level, game: this.game, last: this.last } satisfies Saved)
  }

  private tap(n: number): void {
    this.focus = n
    if (this.busy) return this.say(this.s.turn === "cpu" ? "The computer is thinking." : "Your marble is still moving.")
    if (this.s.over) return this.say("This game is over. Choose New game to play again.")
    if (this.s.turn !== "you") return
    const x = this.s.board[n]
    if (this.sel !== null) {
      const m = movesFrom(this.s.board, this.sel).find((mv) => mv.to === n)
      if (m) return this.move(m)
      if (n === this.sel) { this.sel = null; this.render(); return this.say("Marble put back.") }
    }
    if (x === "you") {
      const mine = movesFrom(this.s.board, n)
      if (!mine.length) { this.sel = null; this.render(); return this.say("That marble has nowhere to go right now.") }
      this.sel = n; this.hint = null
      this.render()
      const jumps = mine.filter((m) => m.path.length > 1 || !isStep(m)).length
      return this.say(`Marble picked up. ${mine.length} ${mine.length === 1 ? "hole" : "holes"} to move to, marked with dots${jumps ? `, ${jumps} of them by jumping` : ""}.`)
    }
    if (this.sel !== null) return this.say("Your marble can't get there. Choose a hole with a dot.")
    this.say(x ? "That's the computer's marble. Yours are the blue ones." : "Choose one of your blue marbles first.")
  }

  private move(m: Move): void {
    this.history.push(this.s)
    this.sel = null; this.hint = null; this.last = null
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: RULESET, daily: false }) }
    this.animate(m, () => {
      const hops = m.path.length
      const what = isStep(m) ? "You moved one hole." : `You jumped ${hops} ${hops === 1 ? "time" : "times"}.`
      if (this.s.over) return this.finish(what)
      this.say(`${what} The computer is thinking…`)
      this.computerTurn()
    })
  }

  /** Moves the marble along its route on screen, a hop at a time, then plays the move. */
  private animate(m: Move, done: () => void): void {
    const before = this.s
    this.busy = true
    const steps = this.motion ? m.path : []
    const tick = (i: number): void => {
      if (this.s !== before) return
      if (i < steps.length) { this.hop = { from: m.from, at: steps[i]! }; this.render(); setTimeout(() => tick(i + 1), 220); return }
      this.hop = null
      this.s = play(before, m)
      if (before.turn === "cpu") this.last = m
      this.busy = false
      this.persist()
      this.render()
      done()
    }
    tick(0)
  }

  private computerTurn(): void {
    this.busy = true
    this.render()
    setTimeout(() => {
      const before = this.s
      if (before.turn !== "cpu" || before.over) { this.busy = false; return this.render() }
      const m = computerMove(before, this.level)
      if (this.s !== before) return
      this.animate(m, () => {
        const what = isStep(m) ? "The computer moved one hole." : `The computer jumped ${m.path.length} ${m.path.length === 1 ? "time" : "times"}.`
        if (this.s.over) return this.finish(what)
        this.say(`${what} Your turn. Its last move is marked in yellow.`)
      })
    }, this.motion ? 450 : 120)
  }

  private finish(what: string): void {
    const o = this.s.over!
    gameEvent({ type: "end", ruleset: RULESET, outcome: o.winner === "you" ? "win" : o.winner === "draw" ? "abandon" : "loss", moves: this.s.moves, hints: this.hints, undos: this.undos, daily: false })
    this.render()
    const why = o.why === "home"
      ? (o.winner === "you" ? "All of the top point is filled: you won!" : "The computer filled the bottom point and won. Undo to try again, or start a new game.")
      : `After 200 moves each, ${o.winner === "you" ? "you are closer to home, so you won!" : o.winner === "cpu" ? "the computer is closer to home, so it won." : "you are just as close as each other: it's a draw."}`
    this.say(`${what} ${why}`)
  }

  private undo(): void {
    if (this.busy) return this.say("Wait for the computer's move, then Undo.")
    const prev = this.history.pop()
    if (!prev) return this.say("Nothing to undo.")
    this.undos++
    this.s = prev
    this.sel = null; this.hint = null; this.last = null
    this.persist()
    this.render()
    this.say("Your last move and the computer's reply are taken back. Your turn.")
  }

  private showHint(): void {
    if (this.busy || this.s.over || this.s.turn !== "you") return
    const m = hintMove(this.s)
    this.hints++
    this.hint = m
    this.sel = null
    this.render()
    this.say(`Hint: move the outlined marble to the outlined hole${isStep(m) ? "" : `, jumping ${m.path.length} ${m.path.length === 1 ? "time" : "times"}`}.`)
  }

  private newGame(): void {
    if (this.sentFirst && !this.s.over) gameEvent({ type: "end", ruleset: RULESET, outcome: "new_game", moves: this.s.moves, hints: this.hints, undos: this.undos })
    this.s = newGame()
    this.history = []
    this.game = newAttempt()
    this.sel = null; this.hint = null; this.last = null; this.busy = false; this.hop = null
    this.sentFirst = false; this.hints = 0; this.undos = 0
    this.persist()
    this.render()
    this.say("New game. Your blue marbles start at the bottom. Move them all into the top point.")
  }

  private onKey(e: KeyboardEvent): void {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-hole]")
    if (!b) return
    const n = Number(b.dataset.hole)
    const { q, r } = HOLES[n]!
    const x = q + r / 2
    let to = -1
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const d = e.key === "ArrowLeft" ? -1 : 1
      to = HOLES.findIndex((h) => h.r === r && h.q === q + d)
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const nr = r + (e.key === "ArrowUp" ? -1 : 1)
      let bestD = Infinity
      HOLES.forEach((h, i) => { if (h.r === nr) { const d = Math.abs(h.q + h.r / 2 - x) + (h.q + h.r / 2 > x ? 0.01 : 0); if (d < bestD) { bestD = d; to = i } } })
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); this.tap(n); this.board.querySelector<HTMLElement>(`[data-hole="${this.focus}"]`)?.focus(); return
    } else if (e.key === "Escape" && this.sel !== null) {
      this.sel = null; this.render(); this.say("Marble put back."); return
    } else return
    e.preventDefault()
    if (to >= 0) { this.focus = to; this.render(); this.board.querySelector<HTMLElement>(`[data-hole="${to}"]`)?.focus() }
  }

  private render(): void {
    gameProgress({ key: SAVE, ruleset: RULESET, id: String(this.game), moves: this.s.moves, started: this.s.moves > 0, won: this.s.over?.winner === "you" })
    const board = this.s.board.slice()
    if (this.hop) { board[this.hop.at] = board[this.hop.from]!; board[this.hop.from] = null }
    const targets = new Set(this.sel !== null && !this.busy ? movesFrom(this.s.board, this.sel).map((m) => m.to) : [])
    const movable = this.s.turn === "you" && !this.busy && !this.s.over ? new Set(legalMoves(this.s).map((m) => m.from)) : new Set<number>()
    const lastHoles = new Set(this.last ? [this.last.from, this.last.to] : [])
    const hintHoles = new Set(this.hint ? [this.hint.from, this.hint.to] : [])
    this.board.querySelectorAll<HTMLElement>("[data-hole]").forEach((b) => {
      const n = Number(b.dataset.hole)
      const x = board[n]
      b.innerHTML = x ? `<span class="cc-marble is-${x}" aria-hidden="true"></span>` : ""
      b.classList.toggle("is-selected", n === this.sel || (this.hop !== null && n === this.hop.at))
      b.classList.toggle("is-target", targets.has(n))
      b.classList.toggle("is-last", lastHoles.has(n))
      b.classList.toggle("is-hint", hintHoles.has(n))
      b.tabIndex = n === this.focus ? 0 : -1
      const who = x === "you" ? "your marble" : x === "cpu" ? "computer's marble" : "empty"
      const extra = `${n === this.sel ? ", picked up" : ""}${targets.has(n) ? ", you can move here" : ""}${x === "you" && !movable.has(n) && this.s.turn === "you" ? ", can't move" : ""}${lastHoles.has(n) ? ", computer's last move" : ""}`
      b.setAttribute("aria-label", `${holeName(n)}: ${who}${extra}`)
    })
    const count = this.root.querySelector<HTMLElement>("[data-cc-count]")
    if (count) count.textContent = `Home: you ${home(this.s.board, "you")} of 10 · computer ${home(this.s.board, "cpu")} of 10`
    this.root.classList.toggle("is-thinking", this.busy)
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
}

/** A one-hole step rather than a jump (a single jump also lands two holes away). */
const isStep = (m: Move): boolean => {
  const a = HOLES[m.from]!, b = HOLES[m.to]!
  return m.path.length === 1 && Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.q + a.r - b.q - b.r)) === 1
}
